import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Send, MessageCircle, Leaf, Heart, RotateCcw, Briefcase, GraduationCap, Sun, Plane, Clock, DollarSign, Info, Mic } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import FactoryHypeContactModal from "../components/FactoryHypeContactModal";
import VoiceRecorder from "../components/VoiceRecorder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_ID = "factoryhype";

// Leonie - FactoryHype chatbot avatar
const BOT_AVATAR = "https://static.prod-images.emergentagent.com/jobs/a1355f58-fa33-4368-994e-884784b84e2d/images/35ff361465372cb2a15bf2f9d4a2b4c726defacdcb87abb1a9362a4c6a884031.png";

// Job variant icons
const VARIANT_ICONS = {
  nebenjob: Briefcase,
  studentenjob: GraduationCap,
  ferienjob: Sun,
  promotour: Plane,
  flexibel: Clock,
  regelmaessig: DollarSign,
  mehr_infos: Info
};

const TypingIndicator = () => (
  <div className="flex items-start gap-3 animate-fade-in">
    <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md bg-emerald-100">
      <img src={BOT_AVATAR} alt="Leonie" className="w-full h-full object-cover" />
    </div>
    <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm shadow-sm px-4 py-3">
      <div className="flex gap-1.5">
        <span className="typing-dot w-2 h-2 bg-emerald-400 rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-emerald-400 rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-emerald-400 rounded-full"></span>
      </div>
    </div>
  </div>
);

// Helper function to render text with **bold** markdown support
const renderMessageContent = (content) => {
  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const ChatMessage = ({ message, index }) => {
  const isBot = message.role === "assistant";
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className={`flex items-start gap-3 ${isBot ? "" : "flex-row-reverse"}`}
      data-testid={`chat-message-${message.role}-${index}`}
    >
      {isBot && (
        <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md bg-emerald-100">
          <img src={BOT_AVATAR} alt="Leo" className="w-full h-full object-cover" />
        </div>
      )}
      <div
        className={`max-w-[85%] px-4 py-3 shadow-sm ${
          isBot
            ? "bg-white border border-slate-100 text-slate-800 rounded-2xl rounded-tl-sm"
            : "bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl rounded-tr-sm"
        }`}
      >
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{renderMessageContent(message.content)}</p>
      </div>
    </motion.div>
  );
};

// Quick action buttons for job types - Dynamic based on settings
const QuickActionButtons = ({ onAction, variants }) => {
  if (!variants || variants.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {variants.map((variant) => {
        const Icon = VARIANT_ICONS[variant.id] || Briefcase;
        return (
          <Button
            key={variant.id}
            variant="outline"
            size="sm"
            onClick={() => onAction(variant.name)}
            className="text-xs border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-1.5"
            data-testid={`quick-action-${variant.id}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {variant.name}
          </Button>
        );
      })}
    </div>
  );
};

export default function FactoryHypeChatPage() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showContactButton, setShowContactButton] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [jobVariants, setJobVariants] = useState([]);
  const [selectedJobType, setSelectedJobType] = useState(null);
  const [showJobVariantButtons, setShowJobVariantButtons] = useState(true);
  
  // Voice Recording State
  const [voiceSettings, setVoiceSettings] = useState({ enabled: false, allow_user_choice: false, voice_mandatory: false, voice_mandatory_text: "", max_duration_seconds: 30, prompt_text: "" });
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [voiceCompleted, setVoiceCompleted] = useState(false);
  const [showVoiceChoiceButtons, setShowVoiceChoiceButtons] = useState(false);
  
  // Qualifizierungs-Buttons State
  const [currentQualiStep, setCurrentQualiStep] = useState(null); // "q1", "q2", "q3", "q4", "q4_reassure"
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const initializedRef = useRef(false);

  // Qualifizierungs-Button Optionen für alle Job-Typen
  const QUALI_BUTTONS = {
    // ==================== PROMOTOUR ====================
    promotour_q1: {
      question: "Bist du ein offener Typ, der gerne mit Menschen quatscht?",
      options: [
        { text: "Ja, auf jeden Fall!", value: "Ja, auf jeden Fall!" },
        { text: "Denke, kann ich mir aneignen", value: "Denke, kann ich mir aneignen" }
      ]
    },
    promotour_q2: {
      question: "Du bist bei uns immer **3–4 Wochen am Stück auf Tour** und hast danach **frei – so lange, bis du wieder die nächste Tour machen möchtest**. Passt das grundsätzlich für dich?",
      options: [
        { text: "Ja, passt perfekt!", value: "Ja, passt perfekt!" },
        { text: "Nein, lieber lokal arbeiten", value: "Nein, lieber lokal arbeiten" }
      ]
    },
    promotour_q3: {
      question: "Wann könntest du starten?",
      options: [
        { text: "Sofort / Diese Woche", value: "Sofort / Diese Woche" },
        { text: "In 2 Wochen", value: "In 2 Wochen" },
        { text: "In 1 Monat", value: "In 1 Monat" },
        { text: "Später / Noch unklar", value: "Später / Noch unklar" }
      ]
    },
    promotour_q4: {
      question: "Wenn du gleich das Kontaktformular ausfüllst, melden wir uns **heute oder morgen** telefonisch bei dir und vereinbaren mit dir einen **kurzen Video-Call** zum Kennenlernen – **ganz easy auch übers Handy**. Wenn alles passt, schicken wir dir danach **direkt den Arbeitsvertrag** zu. Passt das für dich?",
      options: [
        { text: "Ja, passt!", value: "Ja, passt!" },
        { text: "Kenne ich noch nicht so", value: "Kenne ich noch nicht so" }
      ]
    },
    // ==================== NEBENJOB / STUDENTENJOB ====================
    nebenjob_q1: {
      question: "Bist du ein offener Typ, der gerne mit Menschen quatscht?",
      options: [
        { text: "Ja, auf jeden Fall!", value: "Ja, auf jeden Fall!" },
        { text: "Denke, kann ich mir aneignen", value: "Denke, kann ich mir aneignen" }
      ]
    },
    nebenjob_q2: {
      question: "In welcher Stadt möchtest du arbeiten?",
      options: [
        { text: "Berlin", value: "Berlin" },
        { text: "Hamburg", value: "Hamburg" },
        { text: "München", value: "München" },
        { text: "Köln", value: "Köln" },
        { text: "Frankfurt", value: "Frankfurt" },
        { text: "Düsseldorf", value: "Düsseldorf" },
        { text: "Andere Stadt", value: "Andere Stadt" }
      ]
    },
    nebenjob_q3: {
      question: "An welchen Tagen hast du Zeit?",
      options: [
        { text: "Nur Wochenende", value: "Nur Wochenende" },
        { text: "Unter der Woche", value: "Unter der Woche" },
        { text: "Ganz flexibel", value: "Ganz flexibel" }
      ]
    },
    nebenjob_q4: {
      question: "Wann könntest du starten?",
      options: [
        { text: "Sofort / Diese Woche", value: "Sofort / Diese Woche" },
        { text: "In 2 Wochen", value: "In 2 Wochen" },
        { text: "In 1 Monat", value: "In 1 Monat" },
        { text: "Später / Noch unklar", value: "Später / Noch unklar" }
      ]
    },
    nebenjob_q5: {
      question: "Wenn du das Kontaktformular ausfüllst, melden wir uns telefonisch bei dir für einen kurzen Video-Call zum Kennenlernen. Passt das für dich?",
      options: [
        { text: "Ja, passt!", value: "Ja, passt!" },
        { text: "Kenne ich noch nicht so", value: "Kenne ich noch nicht so" }
      ]
    },
    // ==================== FERIENJOB ====================
    ferienjob_q1: {
      question: "Bist du ein offener Typ, der gerne mit Menschen quatscht?",
      options: [
        { text: "Ja, auf jeden Fall!", value: "Ja, auf jeden Fall!" },
        { text: "Denke, kann ich mir aneignen", value: "Denke, kann ich mir aneignen" }
      ]
    },
    ferienjob_q2: {
      question: "Wann sind deine nächsten Ferien?",
      options: [
        { text: "Osterferien", value: "Osterferien" },
        { text: "Sommerferien", value: "Sommerferien" },
        { text: "Herbstferien", value: "Herbstferien" },
        { text: "Winterferien", value: "Winterferien" },
        { text: "Semesterferien", value: "Semesterferien" }
      ]
    },
    ferienjob_q3: {
      question: "Möchtest du lokal arbeiten oder auf Promotour gehen?",
      options: [
        { text: "Lokal in meiner Stadt", value: "Lokal in meiner Stadt" },
        { text: "Auf Promotour (mehr Abenteuer!)", value: "Auf Promotour" }
      ]
    },
    ferienjob_q4: {
      question: "Wann könntest du starten?",
      options: [
        { text: "Sofort / Diese Woche", value: "Sofort / Diese Woche" },
        { text: "Zum Ferienbeginn", value: "Zum Ferienbeginn" },
        { text: "Später / Noch unklar", value: "Später / Noch unklar" }
      ]
    },
    ferienjob_q5: {
      question: "Wenn du das Kontaktformular ausfüllst, melden wir uns telefonisch bei dir für einen kurzen Video-Call zum Kennenlernen. Passt das für dich?",
      options: [
        { text: "Ja, passt!", value: "Ja, passt!" },
        { text: "Kenne ich noch nicht so", value: "Kenne ich noch nicht so" }
      ]
    }
  };

  // Alte Promotour-only Keys für Rückwärtskompatibilität
  const LEGACY_QUALI_MAPPING = {
    "q1": "promotour_q1",
    "q2": "promotour_q2", 
    "q3": "promotour_q3",
    "q4": "promotour_q4"
  };

  // Function to show contact form or voice recorder first
  const proceedToContactForm = () => {
    // If voice is enabled and not yet completed
    if (voiceSettings.enabled && !voiceCompleted) {
      if (voiceSettings.voice_mandatory) {
        // Voice is mandatory - show motivating text + recorder, no skip possible
        const mandatoryText = voiceSettings.voice_mandatory_text || "Bevor es weitergeht, nimm bitte eine kurze persönliche Sprachnachricht auf. Erzähl uns in 2-3 Sätzen, warum du den Job gerne machen möchtest. Das hilft uns, dich besser kennenzulernen und gibt dir einen echten Vorteil gegenüber anderen Bewerbern.";
        setMessages(prev => [...prev, { role: 'assistant', content: mandatoryText }]);
        setShowVoiceRecorder(true);
        setShowContactButton(false);
        setShowVoiceChoiceButtons(false);
      } else if (voiceSettings.allow_user_choice) {
        // Show choice buttons instead of voice recorder directly
        setShowVoiceChoiceButtons(true);
        setShowContactButton(false);
        setShowVoiceRecorder(false);
      } else {
        // Show voice recorder directly (no choice)
        setShowVoiceRecorder(true);
        setShowContactButton(false);
        setShowVoiceChoiceButtons(false);
      }
    } else {
      // Voice not enabled or already completed - show contact form directly
      setShowContactButton(true);
      setShowVoiceRecorder(false);
      setShowVoiceChoiceButtons(false);
    }
  };

  // Handle user choice: Voice or Contact form
  const handleVoiceChoice = (choice) => {
    setShowVoiceChoiceButtons(false);
    if (choice === 'voice') {
      // User wants to record voice
      setShowVoiceRecorder(true);
      setShowContactButton(false);
    } else {
      // User wants to skip voice - open contact form DIRECTLY
      setVoiceCompleted(true);
      setShowVoiceRecorder(false);
      setShowContactButton(false);
      // Open contact modal immediately
      setIsContactModalOpen(true);
    }
  };
  
  // Helper to save message to backend
  const saveMessageToBackend = async (message) => {
    if (!sessionId) return;
    try {
      await axios.post(`${API}/chat/save-message`, {
        session_id: sessionId,
        project_id: PROJECT_ID,
        message: message
      });
    } catch (err) {
      console.error("Error saving message:", err);
    }
  };

  // Handle voice recording completion
  const handleVoiceComplete = async (voiceData) => {
    setVoiceCompleted(true);
    setShowVoiceRecorder(false);
    
    // Add user message showing they recorded
    const userVoiceMessage = {
      role: "user",
      content: "🎤 Sprachnachricht aufgenommen"
    };
    setMessages(prev => [...prev, userVoiceMessage]);
    await saveMessageToBackend(userVoiceMessage);
    
    // Add confirmation message WITHOUT transcript (transcript only visible in admin)
    const confirmMessage = {
      role: "assistant", 
      content: `Super, danke dir für deine Sprachnachricht! 🎤\n\nJetzt bitte noch **unbedingt das Kontaktformular ausfüllen** – erst dann ist deine Bewerbung vollständig im System.\n\nAuch wenn du schon mal in einem anderen Chat Daten eingetragen hast, ist dieser Schritt hier jetzt trotzdem noch nötig.`
    };
    setMessages(prev => [...prev, confirmMessage]);
    await saveMessageToBackend(confirmMessage);
    
    // Now show contact button
    setShowContactButton(true);
  };

  // Initial choice buttons component - NOT USED ANYMORE, keeping for reference
  // const InitialChoiceButtons = ({ onChoice }) => ( ... );

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 150);
    });
  };

  // Initialize chat session
  useEffect(() => {
    const initChat = async () => {
      if (initializedRef.current) return;
      initializedRef.current = true;
      
      // New welcome message - direct job variant selection
      const greeting = {
        role: "assistant",
        content: `Hey 👋 Leonie hier, von FactoryHype.

Kurz gesagt: Wenn du keine Lust auf langweiligen Alltag hast, aber trotzdem **nicht dauerhaft weg von zuhause** sein willst, könnte das hier **richtig gut zu dir passen**.

So läuft's: **3–4 Wochen auf Tour**, danach wieder **zuhause und frei**. Wann du wieder losziehst, **entscheidest du selbst**.

Du kannst dabei **richtig gutes Geld verdienen**, neue Städte sehen und trotzdem noch **dein eigenes Leben** haben.

Was passt gerade am ehesten zu dir? 👇`
      };
      
      try {
        // Create session FIRST
        const sessionResponse = await axios.post(`${API}/chat/session?project_id=${PROJECT_ID}`);
        setSessionId(sessionResponse.data.session_id);
        
        // Fetch voice settings
        try {
          const voiceResponse = await axios.get(`${API}/voice-settings/${PROJECT_ID}`);
          setVoiceSettings(voiceResponse.data);
        } catch (err) {
          console.log("Voice settings not available");
        }
        
        // Fetch job variants settings
        let variants = [];
        try {
          const variantsResponse = await axios.get(`${API}/job-variants/${PROJECT_ID}`);
          variants = variantsResponse.data.variants || [];
        } catch (err) {
          console.log("Using default job variants");
          // Default variants if API fails - must match LIVE admin settings
          variants = [
            { id: "promotour", name: "Promojob auf Reisen Vollzeit", active: true },
            { id: "flexibel", name: "Eher flexibel & mit Freizeiten", active: true },
            { id: "regelmaessig", name: "Regelmäßig unterwegs & gutes Geld", active: true },
            { id: "mehr_infos", name: "Erstmal mehr Infos", active: true }
          ];
        }
        
        setJobVariants(variants);
        setMessages([greeting]);
        setShowJobVariantButtons(true);
        setIsInitializing(false);
      } catch (error) {
        console.error("Error initializing chat:", error);
        // Show greeting even on error with default buttons
        setMessages([greeting]);
        // Set default variants so buttons still appear - must match LIVE admin settings
        setJobVariants([
          { id: "promotour", name: "Promojob auf Reisen Vollzeit", active: true },
          { id: "flexibel", name: "Eher flexibel & mit Freizeiten", active: true },
          { id: "regelmaessig", name: "Regelmäßig unterwegs & gutes Geld", active: true },
          { id: "mehr_infos", name: "Erstmal mehr Infos", active: true }
        ]);
        setShowJobVariantButtons(true);
        setIsInitializing(false);
      }
    };
    
    initChat();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Reset chat function
  const resetChat = async () => {
    initializedRef.current = false;
    setMessages([]);
    setSessionId(null);
    setShowContactButton(false);
    setIsSubmitted(false);
    setShowQuickActions(false);
    setShowJobVariantButtons(true);
    setSelectedJobType(null);
    setCurrentQualiStep(null);
    setIsInitializing(true);
    // Reset voice state for new chat
    setVoiceCompleted(false);
    setShowVoiceRecorder(false);
    
    // New welcome message - direct job variant selection
    const greeting = {
      role: "assistant",
      content: `Hey 👋 Leonie hier, von FactoryHype.

Kurz gesagt: Wenn du keine Lust auf langweiligen Alltag hast, aber trotzdem **nicht dauerhaft weg von zuhause** sein willst, könnte das hier **richtig gut zu dir passen**.

So läuft's: **3–4 Wochen auf Tour**, danach wieder **zuhause und frei**. Wann du wieder losziehst, **entscheidest du selbst**.

Du kannst dabei **richtig gutes Geld verdienen**, neue Städte sehen und trotzdem noch **dein eigenes Leben** haben.

Was passt gerade am ehesten zu dir? 👇`
    };
    
    try {
      // Create session FIRST
      const response = await axios.post(`${API}/chat/session?project_id=${PROJECT_ID}`);
      setSessionId(response.data.session_id);
      initializedRef.current = true;
      
      // Fetch voice settings
      try {
        const voiceResponse = await axios.get(`${API}/voice-settings/${PROJECT_ID}`);
        setVoiceSettings(voiceResponse.data);
      } catch (err) {
        console.log("Voice settings not available");
      }
      
      // Fetch job variants
      try {
        const variantsResponse = await axios.get(`${API}/job-variants/${PROJECT_ID}`);
        setJobVariants(variantsResponse.data.variants || []);
      } catch (err) {
        console.log("Using existing job variants");
      }
      
      setMessages([greeting]);
      setShowJobVariantButtons(true);
    } catch (error) {
      console.error("Error resetting chat:", error);
      setMessages([greeting]);
      setShowJobVariantButtons(true);
    }
    
    setIsInitializing(false);
  };

  // Handler für Job-Varianten Auswahl - direkt nach Willkommensnachricht
  const handleJobVariantChoice = (variantName, variantId) => {
    setShowJobVariantButtons(false);
    const textLower = variantName.toLowerCase();
    
    // Neue Varianten die zum Promotour-Flow führen
    if (variantId === "flexibel" || variantId === "regelmaessig" || variantId === "mehr_infos") {
      setSelectedJobType("promotour");
      sendMessageWithQualiFlow(variantName, "promotour_q1", "promotour");
      return;
    }
    
    // Bestimme Job-Typ und starte entsprechenden Quali-Flow
    if (textLower.includes("promotour") || textLower.includes("promojob") || textLower.includes("reisen")) {
      setSelectedJobType("promotour");
      sendMessageWithQualiFlow(variantName, "promotour_q1", "promotour");
    } else if (textLower.includes("nebenjob")) {
      setSelectedJobType("nebenjob");
      sendMessageWithQualiFlow(variantName, "nebenjob_q1", "nebenjob");
    } else if (textLower.includes("studentenjob") || textLower.includes("student")) {
      setSelectedJobType("studentenjob");
      sendMessageWithQualiFlow(variantName, "nebenjob_q1", "nebenjob"); // Gleicher Flow wie Nebenjob
    } else if (textLower.includes("ferienjob") || textLower.includes("ferien")) {
      setSelectedJobType("ferienjob");
      sendMessageWithQualiFlow(variantName, "ferienjob_q1", "ferienjob");
    } else {
      // Fallback: normale sendMessage
      sendMessage(variantName);
    }
  };

  // Handler für Quali-Button Klicks
  const handleQualiButtonClick = async (buttonValue, currentStep) => {
    setCurrentQualiStep(null);
    
    const userMessage = { role: "user", content: buttonValue };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      // "Kenne ich noch nicht so" - Beruhigung + Formular
      if (currentStep.endsWith("_q4") || currentStep.endsWith("_q5")) {
        if (buttonValue === "Kenne ich noch nicht so") {
          let reassureContent;
          if (voiceSettings.enabled && voiceSettings.voice_mandatory && !voiceCompleted) {
            reassureContent = `Kein Problem 😊\n\nDas ist ganz unkompliziert und funktioniert auch easy über dein Handy. Wir erklären dir vorher alles in Ruhe.`;
          } else {
            reassureContent = `Kein Problem 😊\n\nDas ist ganz unkompliziert und funktioniert auch easy über dein Handy. Wir erklären dir vorher alles in Ruhe.\n\nPerfekt! 🎉\nBitte bestätige jetzt noch kurz deine Kontaktdaten, damit wir dich sicher erreichen können.\n\nDas dauert nur einen Moment. 💚`;
          }
          const reassureMessage = {
            role: "assistant",
            content: reassureContent
          };
          
          // Speichere in DB
          await axios.post(`${API}/chat/quali-message`, {
            session_id: sessionId,
            user_message: buttonValue,
            bot_response: reassureMessage.content
          });
          
          setMessages(prev => [...prev, reassureMessage]);
          proceedToContactForm();
          setIsLoading(false);
          return;
        }
      }
      
      // "Nein, lieber lokal" bei Promotour
      if (currentStep === "promotour_q2" && buttonValue === "Nein, lieber lokal arbeiten") {
        const localMessage = {
          role: "assistant",
          content: `Kein Problem! 😊 Wir haben auch lokale Jobs in vielen Städten.\n\nSchau dir gerne unsere anderen Optionen an – Nebenjob oder Studentenjob sind perfekt für lokales Arbeiten!`
        };
        
        // Speichere in DB
        await axios.post(`${API}/chat/quali-message`, {
          session_id: sessionId,
          user_message: buttonValue,
          bot_response: localMessage.content
        });
        
        setMessages(prev => [...prev, localMessage]);
        setShowJobVariantButtons(true);
        setSelectedJobType(null);
        setIsLoading(false);
        return;
      }
      
      // Flow-Definitionen für jeden Job-Typ
      const FLOWS = {
        promotour: ["promotour_q1", "promotour_q2", "promotour_q3", "promotour_q4", "done"],
        nebenjob: ["nebenjob_q1", "nebenjob_q2", "nebenjob_q3", "nebenjob_q4", "nebenjob_q5", "done"],
        studentenjob: ["nebenjob_q1", "nebenjob_q2", "nebenjob_q3", "nebenjob_q4", "nebenjob_q5", "done"], // Same as nebenjob
        ferienjob: ["ferienjob_q1", "ferienjob_q2", "ferienjob_q3", "ferienjob_q4", "ferienjob_q5", "done"]
      };
      
      // Finde nächsten Step
      const jobType = selectedJobType || "promotour";
      const flow = FLOWS[jobType] || FLOWS.promotour;
      const currentIndex = flow.indexOf(currentStep);
      const nextStep = flow[currentIndex + 1];
      
      let botResponse;
      
      if (nextStep === "done") {
        // If voice is mandatory, skip contact message and go straight to voice recorder
        if (voiceSettings.enabled && voiceSettings.voice_mandatory && !voiceCompleted) {
          // Save the user's last button value with the proper voice-prompt text
          // (so the transcript shows a clean transition into the voice recorder)
          const voicePromptText = voiceSettings.voice_mandatory_text
            || "Bevor's losgeht: Nimm bitte kurz eine Sprachnachricht auf. 2-3 Sätze, warum genau du der/die Richtige bist – das hebt dich aus der Masse raus.";
          await axios.post(`${API}/chat/quali-message`, {
            session_id: sessionId,
            user_message: buttonValue,
            bot_response: voicePromptText
          });
          proceedToContactForm();
        } else {
          botResponse = `Perfekt! 🎉\nBitte bestätige jetzt noch kurz deine Kontaktdaten, damit wir dich sicher erreichen können.\n\nDas dauert nur einen Moment. 💚`;
          
          // Speichere in DB
          await axios.post(`${API}/chat/quali-message`, {
            session_id: sessionId,
            user_message: buttonValue,
            bot_response: botResponse
          });
          
          const finalMessage = { role: "assistant", content: botResponse };
          setMessages(prev => [...prev, finalMessage]);
          proceedToContactForm();
        }
      } else {
        const nextQuestion = QUALI_BUTTONS[nextStep];
        if (nextQuestion) {
          const intro = getQualiIntroText(currentStep, buttonValue, jobType);
          botResponse = `${intro}\n\n**${nextQuestion.question}**`;
          
          // Speichere in DB
          await axios.post(`${API}/chat/quali-message`, {
            session_id: sessionId,
            user_message: buttonValue,
            bot_response: botResponse
          });
          
          const botMessage = { role: "assistant", content: botResponse };
          setMessages(prev => [...prev, botMessage]);
          setCurrentQualiStep(nextStep);
        }
      }
    } catch (error) {
      console.error("Error in quali flow:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Intro-Text zwischen Quali-Fragen
  const getQualiIntroText = (prevStep, answer, jobType) => {
    if (prevStep.includes("_q1")) return "Super, das ist die richtige Einstellung! 💪";
    if (prevStep.includes("_q2")) return "Super! 😊";
    if (prevStep.includes("_q3")) return `Super, ${answer} passt! 🎉`;
    if (prevStep.includes("_q4") && jobType !== "promotour") return "Perfekt! 😊\n\nDann erkläre ich dir kurz, wie es weitergeht:";
    if (prevStep === "promotour_q3") return `Super, ${answer} passt perfekt! 🎉\n\nDann erkläre ich dir kurz, wie es weitergeht:`;
    return "Super! 😊";
  };

  // Sende Nachricht und starte Quali-Flow
  const sendMessageWithQualiFlow = async (text, startQualiStep, jobType) => {
    if (!text || isLoading || !sessionId) return;
    
    const userMessage = { role: "user", content: text };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      // Erste Antwort basierend auf Job-Typ
      let firstResponse;
      
      if (jobType === "promotour") {
        firstResponse = {
          role: "assistant",
          content: `Nice, dass du dabei bist 🙌

Okay, hier kommt der Deal:

Du bist mit einem Team in verschiedenen Städten unterwegs und machst Promotion für Organisationen wie **WWF**, **SOS-Kinderdörfer** oder **Tierschutzprojekte**. Also **kein sinnloser Kram**, sondern Sachen, **hinter denen man auch stehen kann**. 🌍🐾

Was für dich drin ist:

Unterkunft & Fahrt? **Bezahlt.**
Alleine unterwegs? Nope, **immer im Team.**
4 Wochen dabei = **2.688 € fix**, mit Boni bis zu **4.927 €**
Danach? **Ab nach Hause**, Kohle ausgeben, chillen – und **selbst entscheiden**, wann du wieder Lust hast.

Klingt machbar? Dann lass uns kurz checken, ob's passt:
Bist du ein offener Typ und hast Lust, mit Menschen zu sprechen?`
        };
      } else if (jobType === "nebenjob") {
        firstResponse = {
          role: "assistant",
          content: `Hey! 😊\n\nCool, dass du dich für den Nebenjob interessierst! Bei uns machst du Promotion für coole NGOs wie WWF und SOS-Kinderdörfer – direkt in deiner Stadt! 🌍\n\n**Das Wichtigste:**\n- Du arbeitest **lokal** (keine Reisen nötig!)\n- **Flexible Zeiten** (du bestimmst wann!)\n- Verdienst: **14€/Stunde + Prämien**\n- **Perfekt neben Schule, Studium oder Hauptjob**\n\n**${QUALI_BUTTONS[startQualiStep].question}**`
        };
      } else if (jobType === "ferienjob") {
        firstResponse = {
          role: "assistant",
          content: `Hey! 😊\n\nSuper, Ferienjob ist eine tolle Wahl! In den Ferien **richtig Geld verdienen** und dabei **Gutes tun**! 🌍\n\n**Das Wichtigste:**\n- Promotion für NGOs wie **WWF** und **SOS-Kinderdörfer**\n- **Lokal oder auf Promotour** – du entscheidest!\n- Verdienst: **14€/Stunde + Prämien**\n- **Perfekt um in kurzer Zeit viel zu verdienen**\n\n**${QUALI_BUTTONS[startQualiStep].question}**`
        };
      } else {
        firstResponse = {
          role: "assistant", 
          content: `Hey! 😊\n\nToll, dass du Interesse hast!\n\n**${QUALI_BUTTONS[startQualiStep]?.question || "Bist du ein offener Typ, der gerne mit Menschen quatscht?"}**`
        };
      }
      
      // Speichere die Nachrichten in der DB OHNE LLM aufzurufen
      await axios.post(`${API}/chat/quali-message`, {
        session_id: sessionId,
        user_message: text,
        bot_response: firstResponse.content
      });
      
      setMessages(prev => [...prev, firstResponse]);
      setCurrentQualiStep(startQualiStep);
    } catch (error) {
      console.error("Error starting quali flow:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (messageText = null) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading || !sessionId) return;
    
    setInputValue("");
    setShowQuickActions(false);
    
    const textLower = text.toLowerCase();
    
    // WICHTIG: Wenn wir im Quali-Flow sind, behandle getippte Antworten als Button-Klicks
    if (currentQualiStep && QUALI_BUTTONS[currentQualiStep]) {
      const options = QUALI_BUTTONS[currentQualiStep].options;
      
      // Versuche die getippte Antwort einem Button zuzuordnen
      let matchedOption = null;
      for (const option of options) {
        const optionLower = option.text.toLowerCase();
        // Exakte Übereinstimmung oder enthält den Text
        if (textLower === optionLower || 
            textLower.includes(optionLower) || 
            optionLower.includes(textLower) ||
            // Auch "ja" allein sollte zur ersten (positiven) Option matchen
            (textLower === "ja" && options.indexOf(option) === 0) ||
            (textLower.includes("ja") && textLower.length < 20 && options.indexOf(option) === 0) ||
            (textLower.includes("nein") && options.indexOf(option) === 1) ||
            (textLower.includes("passt") && options.indexOf(option) === 0)) {
          matchedOption = option;
          break;
        }
      }
      
      if (matchedOption) {
        // Behandle als Button-Klick
        handleQualiButtonClick(matchedOption.value, currentQualiStep);
        return;
      }
      // Wenn keine Übereinstimmung, zeige die Nachricht und zeige Buttons erneut
    }
    
    // Check if this is a job type selection (nur wenn noch kein Job-Typ ausgewählt)
    if (!selectedJobType) {
      let matchedVariant = null;
      
      // Explizite Matches für verschiedene Schreibweisen
      if (textLower.includes("promotour") || textLower.includes("promojob auf reisen") || textLower.includes("promo tour") || textLower.includes("auf reisen") || textLower.includes("flexibel") || textLower.includes("regelm") || textLower.includes("mehr info")) {
        matchedVariant = "promotour";
      } else if (textLower.includes("nebenjob")) {
        matchedVariant = "nebenjob";
      } else if (textLower.includes("studentenjob") || textLower.includes("student")) {
        matchedVariant = "studentenjob";
      } else if (textLower.includes("ferienjob") || textLower.includes("ferien")) {
        matchedVariant = "ferienjob";
      }
      
      if (matchedVariant) {
        // Starte den Quali-Flow für diesen Job-Typ
        handleJobVariantChoice(text, matchedVariant);
        return;
      }
    }
    
    // ==================== FRAGE WÄHREND QUALI ====================
    // Prüfe ob User während der Quali eine Frage stellt (nicht Kontaktformular-Phase)
    if (currentQualiStep && !showContactButton) {
      const isQuestion = text.includes('?') || 
        textLower.includes('wann') ||
        textLower.includes('wie') ||
        textLower.includes('was') ||
        textLower.includes('warum') ||
        textLower.includes('wo') ||
        textLower.includes('wer') ||
        textLower.includes('wem') ||
        textLower.includes('welche') ||
        textLower.includes('wieviel') ||
        textLower.includes('wie viel') ||
        textLower.includes('kann ich') ||
        textLower.includes('muss ich') ||
        textLower.includes('bin ich') ||
        textLower.includes('verdien') ||
        textLower.includes('gehalt') ||
        textLower.includes('team') ||
        textLower.includes('kollegen') ||
        textLower.includes('allein') ||
        textLower.includes('unterkunft') ||
        textLower.includes('fahrt');
      
      if (isQuestion) {
        const userMessage = { role: "user", content: text };
        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);
        
        // Hole die aktuelle Quali-Frage, um sie am Ende zu wiederholen
        const currentQuestion = QUALI_BUTTONS[currentQualiStep]?.question || "";
        
        try {
          // FAQ Mode mit Hinweis auf aktuelle Quali-Frage
          const response = await axios.post(`${API}/chat/message`, {
            session_id: sessionId,
            message: text,
            faq_mode: true,
            continue_with_question: currentQuestion  // Damit Backend weiß, welche Frage wiederholt werden soll
          });
          
          let answerText = response.data.response;
          
          // Füge die aktuelle Quali-Frage am Ende hinzu
          if (currentQuestion) {
            answerText += `\n\n**Aber zurück zu meiner Frage:** ${currentQuestion}`;
          }
          
          const botMessage = { role: "assistant", content: answerText };
          setMessages(prev => [...prev, botMessage]);
          scrollToBottom();
        } catch (error) {
          console.error("FAQ during quali error:", error);
          let fallbackText = "Gute Frage! Das klären wir am besten im persönlichen Gespräch. 😊";
          if (currentQuestion) {
            fallbackText += `\n\n**Aber zurück zu meiner Frage:** ${currentQuestion}`;
          }
          const fallbackMessage = { role: "assistant", content: fallbackText };
          setMessages(prev => [...prev, fallbackMessage]);
          scrollToBottom();
        } finally {
          setIsLoading(false);
        }
        
        return;
      }
    }
    // ==================== ENDE FRAGE WÄHREND QUALI ====================
    
    // ==================== FRAGE WÄHREND VOICE PFLICHT ====================
    // Wenn Voice Pflicht aktiv ist und Bewerber tippt statt aufzunehmen
    if (showVoiceRecorder && voiceSettings.voice_mandatory && !voiceCompleted) {
      const userMessage = { role: "user", content: text };
      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);
      
      try {
        const response = await axios.post(`${API}/chat/message`, {
          session_id: sessionId,
          message: text,
          faq_mode: true
        });
        
        let answerText = response.data.response;
        answerText += "\n\nAber bitte nimm noch kurz deine Sprachnachricht auf – das ist wirklich wichtig für deine Bewerbung und dauert nur ein paar Sekunden.";
        
        const botMessage = { role: "assistant", content: answerText };
        setMessages(prev => [...prev, botMessage]);
        scrollToBottom();
      } catch (error) {
        const fallbackMessage = { 
          role: "assistant", 
          content: "Gute Frage! Das klären wir im persönlichen Gespräch. 😊\n\nAber bitte nimm noch kurz deine Sprachnachricht auf – das ist wirklich wichtig für deine Bewerbung." 
        };
        setMessages(prev => [...prev, fallbackMessage]);
        scrollToBottom();
      } finally {
        setIsLoading(false);
      }
      
      // Voice recorder bleibt sichtbar
      setShowVoiceRecorder(true);
      return;
    }
    // ==================== ENDE FRAGE WÄHREND VOICE PFLICHT ====================

    // WICHTIG: Wenn das Kontaktformular bereits angeboten wird - FAQ Mode aktivieren
    if (showContactButton) {
      const userMessage = { role: "user", content: text };
      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);
      
      try {
        // FAQ Mode - KI beantwortet Fragen ohne Quali-Flow
        const response = await axios.post(`${API}/chat/message`, {
          session_id: sessionId,
          message: text,
          faq_mode: true  // Aktiviert FAQ-Modus im Backend
        });
        
        const botMessage = { role: "assistant", content: response.data.response };
        setMessages(prev => [...prev, botMessage]);
        scrollToBottom();
      } catch (error) {
        console.error("FAQ mode error:", error);
        // Fallback Antwort
        const fallbackMessage = { 
          role: "assistant", 
          content: "Gute Frage! 😊 Das klären wir am besten im persönlichen Gespräch – da kann ich dir alles genau erklären!" 
        };
        setMessages(prev => [...prev, fallbackMessage]);
        scrollToBottom();
      } finally {
        setIsLoading(false);
      }
      
      return;
    }
    
    const userMessage = { role: "user", content: text };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text
      });
      
      const botMessage = { role: "assistant", content: response.data.response };
      setMessages(prev => [...prev, botMessage]);
      
      // Button einmal gezeigt = bleibt sichtbar
      if (response.data.show_contact_button) {
        proceedToContactForm();
      }
      
      // Wenn noch kein Job-Typ ausgewählt wurde, zeige die Job-Varianten-Buttons wieder
      if (!selectedJobType) {
        setShowJobVariantButtons(true);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage = { 
        role: "assistant", 
        content: "Entschuldigung, da ist etwas schiefgelaufen. Bitte versuche es nochmal! 😊" 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleContactSubmit = async (formData) => {
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        session_id: sessionId,
        project_id: PROJECT_ID
      });
      setIsSubmitted(true);
      setIsContactModalOpen(false);
      
      const thankYouMessage = {
        role: "assistant",
        content: "Super, danke! 🙌\n\n**Deine Bewerbung ist eingegangen.** Wir melden uns **schnellstmöglich** bei dir!\n\nBis bald bei FactoryHype - **zusammen bewegen wir was!** 🌍💚"
      };
      setMessages(prev => [...prev, thankYouMessage]);
    } catch (error) {
      console.error("Error submitting contact:", error);
    }
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
            <Leaf className="w-8 h-8 text-emerald-600 animate-pulse" />
          </div>
          <p className="text-emerald-600 font-medium">Chat wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex flex-col overflow-hidden" data-testid="factoryhype-chat-page">
      {/* Header - Fixed für mobile Tastatur */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-4 shadow-lg">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30 bg-emerald-100 flex-shrink-0">
            <img src={BOT_AVATAR} alt="Leo" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold flex items-center gap-2">
              FactoryHype
              <Leaf className="w-4 h-4 text-emerald-200" />
            </h1>
            <p className="text-xs text-emerald-100">Dein Job mit Sinn</p>
          </div>
          {/* Bewerben Button - blinkt magenta wenn Kontaktformular verfügbar */}
          <button
            onClick={() => setIsContactModalOpen(true)}
            className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors shadow-sm flex-shrink-0 ${
              showContactButton && !isSubmitted
                ? 'bg-fuchsia-500 text-white animate-pulse hover:bg-fuchsia-600'
                : 'bg-white text-emerald-700 hover:bg-emerald-50'
            }`}
            data-testid="apply-button"
          >
            <Briefcase className="w-3.5 h-3.5" />
            <span>Bewerben Job sichern</span>
          </button>
          {/* Chat neu starten Button */}
          <button
            onClick={resetChat}
            className="flex items-center gap-1 px-2 py-1 text-xs text-emerald-100 hover:text-white hover:bg-emerald-600 rounded-lg transition-colors flex-shrink-0"
            title="Chat neu starten"
            data-testid="reset-chat-button"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </header>
      
      {/* Spacer für fixed header */}
      <div className="h-16 flex-shrink-0"></div>
      
      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="t2m-chat-scroll-area">
        <div className="max-w-2xl mx-auto space-y-4 pb-4">
          {messages.map((message, index) => (
            <ChatMessage key={index} message={message} index={index} />
          ))}
          
          {/* Job Variant Buttons after welcome message */}
          {/* Job Variant Buttons - werden angezeigt bis ein Job-Typ ausgewählt wurde */}
          {showJobVariantButtons && !selectedJobType && !isLoading && (
            <div className="flex flex-wrap gap-2 mt-3">
              {jobVariants.map((variant) => {
                const Icon = VARIANT_ICONS[variant.id] || Briefcase;
                return (
                  <Button
                    key={variant.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleJobVariantChoice(variant.name, variant.id)}
                    className="text-xs border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-1.5"
                    data-testid={`job-variant-${variant.id}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {variant.name}
                  </Button>
                );
              })}
            </div>
          )}
          
          {/* Qualifizierungs-Buttons */}
          {currentQualiStep && QUALI_BUTTONS[currentQualiStep] && !isLoading && (
            <div className="flex flex-wrap gap-2 mt-3">
              {QUALI_BUTTONS[currentQualiStep].options.map((option, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQualiButtonClick(option.value, currentQualiStep)}
                  className="text-xs border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                  data-testid={`quali-btn-${currentQualiStep}-${idx}`}
                >
                  {option.text}
                </Button>
              ))}
            </div>
          )}
          
          {isLoading && <TypingIndicator />}
          
          <div ref={messagesEndRef} />
        </div>
      </div>
      
      {/* Input Area */}
      <div className="bg-white border-t border-slate-200 p-4 shadow-lg">
        {/* Voice Choice Buttons - User can choose Voice or Contact */}
        {showVoiceChoiceButtons && (
          <div className="max-w-2xl mx-auto mb-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-center text-slate-700 mb-2 font-medium">
                Perfekt! 🎉
              </p>
              <p className="text-center text-slate-600 text-sm mb-4">
                Jetzt kannst du direkt deine Kontaktdaten eintragen oder uns vorher noch kurz eine Sprachnachricht dalassen.
              </p>
              <p className="text-center text-slate-600 text-sm mb-4 bg-white/60 rounded-lg p-3">
                Fast geschafft 😊<br/>
                Eine kurze Sprachnachricht (z.B. warum wieso weshalb gerade Du) ist bei Bewerbungen immer <strong>etwas Besonderes</strong> und wird oft <strong>bevorzugt angeschaut</strong> – dadurch sind <strong>deine Chancen oft noch besser</strong>.<br/>
                Oder trag direkt deine Kontaktdaten ein.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => handleVoiceChoice('voice')}
                  className="w-full rounded-xl bg-purple-600 hover:bg-purple-700 text-white py-3"
                  data-testid="voice-choice-voice"
                >
                  🎤 Kurz Sprachnachricht senden
                </Button>
                <Button
                  onClick={() => handleVoiceChoice('contact')}
                  variant="outline"
                  className="w-full rounded-xl border-emerald-400 text-emerald-700 hover:bg-emerald-50 py-3"
                  data-testid="voice-choice-contact"
                >
                  ⌨️ Lieber direkt Kontaktdaten eintragen
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Voice Recorder */}
        {showVoiceRecorder && (
          <div className="max-w-2xl mx-auto mb-4">
            <VoiceRecorder
              sessionId={sessionId}
              projectId={PROJECT_ID}
              maxDuration={voiceSettings.max_duration_seconds || 30}
              promptText={voiceSettings.prompt_text}
              onComplete={handleVoiceComplete}
            />
          </div>
        )}
        
        {/* Contact Button */}
        {showContactButton && !isSubmitted && !showVoiceRecorder && (
          <div className="max-w-2xl mx-auto mb-3">
            <Button
              onClick={() => setIsContactModalOpen(true)}
              className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white py-3"
              data-testid="contact-button"
            >
              📝 Kontaktdaten eintragen
            </Button>
          </div>
        )}
        
        <div className="max-w-2xl mx-auto flex gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Schreib mir... 💬"
            className="flex-1 rounded-xl border-emerald-200 focus:border-emerald-400 focus:ring-emerald-400"
            disabled={isLoading}
            data-testid="t2m-chat-input"
          />
          <Button
            onClick={() => sendMessage()}
            disabled={!inputValue.trim() || isLoading}
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700"
            data-testid="t2m-send-button"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Footer Disclaimer */}
        <div className="max-w-2xl mx-auto mt-3 px-4 text-center">
          <span className="text-[10px] text-slate-400 leading-tight">
            Made by FactoryHype · KI-gestützter Chat zur ersten Orientierung · Verbindliche Infos erhältst du im persönlichen Gespräch · Deine Daten werden vertraulich zur Bearbeitung deiner Bewerbung verwendet.
          </span>
        </div>
      </div>
      
      {/* Contact Modal */}
      <FactoryHypeContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSubmit={handleContactSubmit}
        sessionId={sessionId}
      />
    </div>
  );
}
